/**
 * @fileoverview Syslog-style logger utility for both Node.js and browser environments
 * with color-coded log levels and environment-aware configuration.
 * @module utils/logger
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @since 0.1.0-beta.7
 * @version 0.1.0-beta.7
 */

/**
 * Syslog-style log levels with numerical values, labels, and terminal color codes
 * @readonly
 * @enum {Object}
 */
const LOG_LEVELS = {
  /** Emergency: system is unusable */
  EMERGENCY: { value: 0, label: 'EMERGENCY', color: '\x1b[41m\x1b[37m' }, // White on red background
  /** Alert: action must be taken immediately */
  ALERT: { value: 1, label: 'ALERT', color: '\x1b[31m' }, // Red
  /** Critical: critical conditions */
  CRITICAL: { value: 2, label: 'CRITICAL', color: '\x1b[35m' }, // Magenta
  /** Error: error conditions */
  ERROR: { value: 3, label: 'ERROR', color: '\x1b[91m' }, // Bright red
  /** Warning: warning conditions */
  WARNING: { value: 4, label: 'WARNING', color: '\x1b[33m' }, // Yellow
  /** Notice: normal but significant condition */
  NOTICE: { value: 5, label: 'NOTICE', color: '\x1b[36m' }, // Cyan
  /** Informational: informational messages */
  INFO: { value: 6, label: 'INFO', color: '\x1b[32m' }, // Green
  /** Debug: debug-level messages */
  DEBUG: { value: 7, label: 'DEBUG', color: '\x1b[90m' } // Gray
};

/** ANSI escape code to reset terminal colors */
const RESET_COLOR = '\x1b[0m';

/**
 * Logger class that provides syslog-style logging capabilities with environment detection
 * and conditional logging based on log levels and environment.
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @since 0.1.0-beta.7
 * @version 0.1.0-beta.7
 */
class Logger {
  /**
   * Creates a new Logger instance
   * @param {string} component - The name of the component that's using this logger
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @since 0.1.0-beta.7
   * @version 0.1.0-beta.7
   */
  constructor(component, enabled = false) {
    /**
     * Component name that will appear in log messages
     * @type {string}
     */
    this.component = component;

    /**
     * Whether the current environment is production
     * @type {boolean}
     */
    this.isProduction = process.env.NODE_ENV === 'production';

    /**
     * Whether the current environment is development
     * @type {boolean}
     */
    this.isDevelopment = process.env.NODE_ENV === 'development';

    /**
     * Whether the current environment is test
     * @type {boolean}
     */
    this.isTest = process.env.NODE_ENV === 'test';

    /**
     * Current log level threshold: messages with level higher than this won't be logged
     * Default is NONE in production, DEBUG in development, and NONE in test
     * @type {number}
     */
    this.level =
      enabled !== true
        ? -1
        : this.isProduction
          ? LOG_LEVELS.INFO.value
          : this.isDevelopment
            ? LOG_LEVELS.DEBUG.value
            : this.isTest
              ? -1
              : -1;

    /**
     * Whether to use colors in log output (true in Node.js, false in browser)
     * @type {boolean}
     */
    this.useColors = this._checkIfColorsEnabled();
  }

  /**
   * Explicitly disable colored output
   * @returns {Logger} - The logger instance for method chaining
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @since 0.1.0-beta.7
   * @version 0.1.0-beta.7
   */
  disableColors() {
    this.useColors = false;
    return this;
  }

  /**
   * Explicitly enable colored output
   * @returns {Logger} - The logger instance for method chaining
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @since 0.1.0-beta.7
   * @version 0.1.0-beta.7
   */
  enableColors() {
    this.useColors = true;
    return this;
  }

  /**
   * Check if colors should be enabled based on current environment
   * @returns {boolean} - True if colors should be enabled
   * @private
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @since 0.1.0-beta.7
   * @version 0.1.0-beta.7
   */
  _checkIfColorsEnabled() {
    return typeof window === 'undefined' && typeof global !== 'undefined';
  }

  /**
   * Set the minimum log level
   * @param {string} levelName - The name of the log level (case-insensitive)
   * @returns {boolean} - True if the level was set successfully, false if the level name is invalid
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @since 0.1.0-beta.7
   * @version 0.1.0-beta.7
   */
  setLevel(levelName) {
    const level = LOG_LEVELS[levelName.toUpperCase()];
    if (level) {
      this.level = level.value;
      return true;
    }
    return false;
  }

  /**
   * Format a log message with timestamp, optional colors, and component name
   * @param {string} level - The level name for this message
   * @param {string} message - The message to format
   * @returns {string} - The formatted message
   * @private
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @since 0.1.0-beta.7
   * @version 0.1.0-beta.7
   */
  _formatMessage(level, message) {
    // Generate a timestamp in ISO format
    const timestamp = new Date().toISOString();

    // Make sure we use the uppercase level name regardless of input
    const upperLevel = level.toUpperCase();
    const logLevel = LOG_LEVELS[upperLevel];

    // Only apply colors if colors are enabled AND we have color information for this level
    if (this.useColors && logLevel && logLevel.color) {
      // Use the raw escape sequence directly to ensure it's properly detected in tests
      return `[${timestamp}]${logLevel.color}[${logLevel.label}]${RESET_COLOR}[${this.component}] ${message}`;
    }

    // No colors - just format the message consistently
    return `[${timestamp}][${upperLevel}][${this.component}] ${message}`;
  }

  /**
   * Determine if a message should be logged based on its level
   * @param {number} levelValue - The numeric value of the log level
   * @returns {boolean} - True if the message should be logged
   * @private
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @since 0.1.0-beta.7
   * @version 0.1.0-beta.7
   */
  _shouldLog(levelValue) {
    return levelValue <= this.level;
  }

  /**
   * Log an emergency message (level 0) - system is unusable
   * @param {string} message - The message to log
   * @param {...any} args - Additional arguments to pass to console.error
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @since 0.1.0-beta.7
   * @version 0.1.0-beta.7
   */
  emergency(...args) {
    if (this._shouldLog(LOG_LEVELS.EMERGENCY.value)) {
      const [message, ...rest] = args;
      console.error(
        this._formatMessage(LOG_LEVELS.EMERGENCY.label, message),
        ...rest
      );
    }
  }

  /**
   * Log an alert message (level 1) - action must be taken immediately
   * @param {string} message - The message to log
   * @param {...any} args - Additional arguments to pass to console.error
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @since 0.1.0-beta.7
   * @version 0.1.0-beta.7
   */
  alert(...args) {
    if (this._shouldLog(LOG_LEVELS.ALERT.value)) {
      const [message, ...rest] = args;
      console.error(
        this._formatMessage(LOG_LEVELS.ALERT.label, message),
        ...rest
      );
    }
  }

  /**
   * Log a critical message (level 2) - critical conditions
   * @param {string} message - The message to log
   * @param {...any} args - Additional arguments to pass to console.error
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @since 0.1.0-beta.7
   * @version 0.1.0-beta.7
   */
  critical(...args) {
    if (this._shouldLog(LOG_LEVELS.CRITICAL.value)) {
      const [message, ...rest] = args;
      console.error(
        this._formatMessage(LOG_LEVELS.CRITICAL.label, message),
        ...rest
      );
    }
  }

  /**
   * Log an error message (level 3) - error conditions
   * @param {string} message - The message to log
   * @param {...any} args - Additional arguments to pass to console.error
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @since 0.1.0-beta.7
   * @version 0.1.0-beta.7
   */
  error(...args) {
    if (this._shouldLog(LOG_LEVELS.ERROR.value)) {
      const [message, ...rest] = args;
      console.error(
        this._formatMessage(LOG_LEVELS.ERROR.label, message),
        ...rest
      );
    }
  }

  /**
   * Log a warning message (level 4) - warning conditions
   * @param {string} message - The message to log
   * @param {...any} args - Additional arguments to pass to console.warn
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @since 0.1.0-beta.7
   * @version 0.1.0-beta.7
   */
  warning(...args) {
    if (this._shouldLog(LOG_LEVELS.WARNING.value)) {
      const [message, ...rest] = args;
      console.warn(
        this._formatMessage(LOG_LEVELS.WARNING.label, message),
        ...rest
      );
    }
  }

  /**
   * Alias for warning method
   * @param {string} message - The message to log
   * @param {...any} args - Additional arguments to pass to console.warn
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @since 0.1.0-beta.7
   * @version 0.1.0-beta.7
   */
  warn(...args) {
    this.warning(...args); // Alias for warning
  }

  /**
   * Log a notice message (level 5) - normal but significant condition
   * @param {string} message - The message to log
   * @param {...any} args - Additional arguments to pass to console.info
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @since 0.1.0-beta.7
   * @version 0.1.0-beta.7
   */
  notice(...args) {
    if (this._shouldLog(LOG_LEVELS.NOTICE.value)) {
      const [message, ...rest] = args;
      console.info(
        this._formatMessage(LOG_LEVELS.NOTICE.label, message),
        ...rest
      );
    }
  }

  /**
   * Log an info message (level 6) - informational messages
   * @param {string} message - The message to log
   * @param {...any} args - Additional arguments to pass to console.info
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @since 0.1.0-beta.7
   * @version 0.1.0-beta.7
   */
  info(...args) {
    if (this._shouldLog(LOG_LEVELS.INFO.value)) {
      const [message, ...rest] = args;
      console.info(
        this._formatMessage(LOG_LEVELS.INFO.label, message),
        ...rest
      );
    }
  }

  /**
   * Log a debug message (level 7) - debug-level messages
   * @param {string} message - The message to log
   * @param {...any} args - Additional arguments to pass to console.log
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @since 0.1.0-beta.7
   * @version 0.1.0-beta.7
   */
  debug(...args) {
    if (this._shouldLog(LOG_LEVELS.DEBUG.value)) {
      const [message, ...rest] = args;
      console.log(
        this._formatMessage(LOG_LEVELS.DEBUG.label, message),
        ...rest
      );
    }
  }
}

/**
 * Create a new logger for a specific component
 * @param {string} component - The name of the component
 * @returns {Logger} - A configured Logger instance
 */
export const createLogger = component => new Logger(component);

/**
 * Default logger instance with the component name "App"
 * @type {Logger}
 */
const defaultLogger = new Logger('App');

export default defaultLogger;
